import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectIsSavingWorkingProject,
  selectWorkingProjectRegistrationQuestions,
  selectWorkingProjectRegistrationSettings,
  selectWorkingProjectRegistrationSteps,
  selectWorkingProjectRequiredRegistrationQuestions,
} from '@/store/slices/project/selectors';
import { getRegistrationQuestions } from '@/store/slices/channel/selectors';
import useLoadRegistrationQuestions from '@/connection/hooks/use-load-registration-questions';
import { GateTypes } from '@/types/registration.types';
import DomainGateTypeOption from './domain-gate-type-option';
import PasscodeTypeOption from './passcode-type-option';
import deriveActiveGateTypes from './helpers/derive-active-gate-types';
import resolveGateToggle from './helpers/resolve-gate-toggle';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { deriveRegistrationUpdate } from '../../helpers/derive-registration-update';
import useOnFinishSave from '@/utils/use-on-finish-save';
import ViewAudienceListsLink from './view-audience-lists-link';
import SettingsToggle from '../../../../controls/settings-toggle';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';

const GatingRegistrationSettings = memo(() => {
  useLoadRegistrationQuestions();

  const isSavingProject = useAppSelector(selectIsSavingWorkingProject);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const registrationSteps = useAppSelector(selectWorkingProjectRegistrationSteps);
  const registrationQuestions = useAppSelector(selectWorkingProjectRegistrationQuestions);
  const requiredRegistrationQuestions = useAppSelector(selectWorkingProjectRequiredRegistrationQuestions);
  const channelRegistrationQuestions = useAppSelector(getRegistrationQuestions);
  const activeGateTypes = useMemo(() => deriveActiveGateTypes(registrationSettings), [registrationSettings]);
  const requirementsMap = registrationSettings?.requirements;
  const sourceBlockedDomains = registrationSettings?.blockedDomainEmails;
  const sourceAllowDomains = requirementsMap?.[GateTypes.domain_email]?.allowedDomains;
  const sourceDomainTab: 'allow' | 'block' =
    registrationSettings?.domain_gate_mode ??
    (sourceAllowDomains?.length ? 'allow' : sourceBlockedDomains?.length ? 'block' : 'allow');

  const { saving: isSavingGatingSettings } = useOnFinishSave({ saveId: 'gating-settings' });

  // Active gates are a SET so legacy events-3 data with multiple coexisting gates (e.g. audience
  // list + blocked domains) all render "on". brandlive-platform persists only one gate, so the
  // first manual toggle collapses the set to a single gate (see resolveGateToggle / saveGateType).
  const [activeGates, setActiveGates] = useState<GateTypes[]>(activeGateTypes);

  useEffect(() => {
    setActiveGates(activeGateTypes);
  }, [activeGateTypes]);

  const isActive = useCallback((gate: GateTypes) => activeGates.includes(gate), [activeGates]);

  const dispatch = useAppDispatch();

  const saveGateType = useCallback(
    (newAccess: GateTypes | null) => {
      if (!registrationSettings) return;
      const update = deriveRegistrationUpdate(
        {
          registration_settings: registrationSettings,
          registration_steps: registrationSteps,
          registration_questions: registrationQuestions,
          required_registration_questions: requiredRegistrationQuestions,
        },
        {
          kind: 'gate',
          gateType: newAccess,
          domainGateMode: sourceDomainTab,
          allowDomains: sourceAllowDomains || [],
          blockDomains: sourceBlockedDomains || [],
          sharedPasscode: requirementsMap?.[GateTypes.shared_passcode]?.sharedPasscode || '',
        },
        channelRegistrationQuestions
      );
      dispatch(saveWorkingProjectByFields({ saveId: 'gating-settings', ...update }));
    },
    [
      channelRegistrationQuestions,
      dispatch,
      registrationQuestions,
      registrationSettings,
      registrationSteps,
      requiredRegistrationQuestions,
      requirementsMap,
      sourceAllowDomains,
      sourceBlockedDomains,
      sourceDomainTab,
    ]
  );

  const handleGateTypeToggle = useCallback(
    (sKey: string, on: boolean) => {
      const gate = Number(sKey) as GateTypes;
      const nextSingle = resolveGateToggle(activeGates, gate, on);
      setActiveGates(nextSingle === null ? [] : [nextSingle]);
      saveGateType(nextSingle);
    },
    [activeGates, saveGateType]
  );

  const isDisabled = isSavingProject || isSavingGatingSettings;

  return (
    <>
      <div className="flex-column gap-8">
        <SettingsToggle
          label="Audience List"
          value={isActive(GateTypes.passcode_list)}
          sKey={GateTypes.passcode_list.toString()}
          onToggle={handleGateTypeToggle}
          disabled={isDisabled}
          tooltipText="Only allow users added to the audience tab to view"
          gap="0"
        >
          <SlideDownCard autoGrow animateOnInitialLoad={false} open={isActive(GateTypes.passcode_list)}>
            <ViewAudienceListsLink show={true} />
          </SlideDownCard>
        </SettingsToggle>

        <SettingsToggle
          label="Password Login"
          value={isActive(GateTypes.password)}
          sKey={GateTypes.password.toString()}
          onToggle={handleGateTypeToggle}
          disabled={isDisabled}
          tooltipText="Require users to create a username & password to join"
        />

        <DomainGateTypeOption
          isSelected={isActive(GateTypes.domain_email)}
          onToggle={(on) => handleGateTypeToggle(GateTypes.domain_email.toString(), on)}
          disabled={isDisabled}
        />

        <PasscodeTypeOption
          isSelected={isActive(GateTypes.shared_passcode)}
          onToggle={(on) => handleGateTypeToggle(GateTypes.shared_passcode.toString(), on)}
          disabled={isDisabled}
        />
      </div>
    </>
  );
});

GatingRegistrationSettings.displayName = 'GatingRegistrationSettings';

export default GatingRegistrationSettings;
